import { NewLocationSurvey } from '../service/newLocationSurveyService';

type LocationSurveyEmailContext = {
    survey: NewLocationSurvey;
    createdByName?: string;
    bankName?: string;
};

const escapeHtml = (value?: string | null): string => {
    if (!value) return 'N/A';
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const renderTableRow = (label: string, value?: string | null) => {
    if (!value || value.trim().length === 0) {
        return '';
    }
    return `
        <tr>
            <td style="padding: 8px 12px; width: 180px; font-weight: 600; vertical-align: top; border-bottom: 1px solid #e6e6e6;">${escapeHtml(label)}</td>
            <td style="padding: 8px 12px; border-bottom: 1px solid #e6e6e6; color: #222;">${escapeHtml(value)}</td>
        </tr>
    `;
};

export const buildLocationSurveyEmail = ({ survey, createdByName, bankName }: LocationSurveyEmailContext) => {
    const subject = `Location Survey Approved - ${survey.surveyId || 'N/A'}`;

    const directions = survey.directions || {};
    const directionRows = ['front', 'back', 'left', 'right']
        .map((key) => {
            const label = key.charAt(0).toUpperCase() + key.slice(1);
            const value = directions[key];
            return renderTableRow(label, value);
        })
        .filter(Boolean)
        .join('');

    const questions = survey.questions || {};
    const questionEntries = [
        { label: 'Fuel Station Available Near Location?', value: questions.fuelStationAvailable },
        { label: 'Space Available for Parking?', value: questions.spaceForParking },
        { label: 'Space Available for Vantage Point?', value: questions.spaceForVantagePoint },
        { label: 'Space Available for Emergency Exit?', value: questions.spaceForEmergencyExit },
        { label: 'Electricity Issue?', value: questions.electricityIssue },
        { label: 'PTCL Line Available?', value: questions.ptclLineAvailable },
        { label: 'Any Other Assessment?', value: questions.otherAssessment }
    ]
        .map(({ label, value }) => renderTableRow(label, value))
        .filter(Boolean)
        .join('');

    const createdDisplay = createdByName || survey.createdByName || survey.createdBy || 'N/A';

    const headerTitle = bankName ? `${escapeHtml(bankName)} - Location Survey Report` : 'Location Survey Report';

    const infoRows = [
        renderTableRow('Survey ID', survey.surveyId),
        renderTableRow('Created By', createdDisplay),
        renderTableRow('Survey Request Date', survey.surveyRequestDate),
        renderTableRow('Survey Date', survey.surveyDate)
    ]
        .filter(Boolean)
        .join('');

    const locationRows = [
        renderTableRow('Location Address', survey.locationAddress || (survey as any).address),
        renderTableRow('Space Consists Of', survey.spaceConsistsOf),
        renderTableRow('Hazardous Area', survey.hazardousArea)
    ]
        .filter(Boolean)
        .join('');

    const remarksRow = survey.remarks
        ? `<div style="margin-top: 16px;">
                <div style="font-weight: 600; margin-bottom: 8px;">Remarks</div>
                <div style="padding: 12px; border: 1px solid #e6e6e6; border-radius: 6px; background: #fafafa; color: #222;">
                    ${escapeHtml(survey.remarks)}
                </div>
            </div>`
        : '';

    const directionsSection = directionRows
        ? `<div style="margin-top: 24px;">
                <div style="font-weight: 600; margin-bottom: 8px;">Directions</div>
                <table style="width: 100%; border: 1px solid #e6e6e6; border-collapse: collapse;">
                    ${directionRows}
                </table>
            </div>`
        : '';

    const questionsSection = questionEntries
        ? `<div style="margin-top: 24px;">
                <div style="font-weight: 600; margin-bottom: 8px;">Assessment Questions</div>
                <table style="width: 100%; border: 1px solid #e6e6e6; border-collapse: collapse;">
                    ${questionEntries}
                </table>
            </div>`
        : '';

    const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${subject}</title>
</head>
<body style="margin: 0; padding: 24px; background: #f5f5f5; font-family: 'Segoe UI', Arial, sans-serif; color: #111;">
    <div style="max-width: 720px; margin: 0 auto; background: #ffffff; border: 1px solid #d8d8d8; border-radius: 8px; overflow: hidden;">
        <div style="padding: 24px 28px; border-bottom: 1px solid #d8d8d8;">
            <div style="font-size: 18px; font-weight: 600; margin-bottom: 4px; color: #111;">${headerTitle}</div>
            <div style="font-size: 13px; color: #333;">${new Date().toLocaleDateString()}</div>
        </div>
        <div style="padding: 24px 28px; font-size: 14px; line-height: 1.6; color: #111;">
            <p style="margin-top: 0;">Dear Team,</p>
            <p>The following location survey has been approved. Kindly review the details below and proceed with the next steps as required.</p>

            <div style="margin-top: 24px;">
                <div style="font-weight: 600; margin-bottom: 8px;">Survey Overview</div>
                <table style="width: 100%; border: 1px solid #d8d8d8; border-collapse: collapse;">
                    ${infoRows}
                </table>
            </div>

            <div style="margin-top: 24px;">
                <div style="font-weight: 600; margin-bottom: 8px;">Location Details</div>
                <table style="width: 100%; border: 1px solid #d8d8d8; border-collapse: collapse;">
                    ${locationRows}
                </table>
            </div>

            ${directionsSection}
            ${questionsSection}
            ${remarksRow}

            <p style="margin-top: 32px;">Please coordinate with the relevant team members if additional information is required.</p>
            <p style="margin-bottom: 0;">Regards,<br />${escapeHtml(createdDisplay)}</p>
        </div>
    </div>
</body>
</html>`;

    return { subject, html };
};
